import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, GridColumn, GridToolbar, GridEditChangeEvent, GridItemChangeEvent, GridCellProps } from '@progress/kendo-react-grid';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
// Removed: import type { GridEditDescriptor } from '@progress/kendo-react-grid'; // Not directly exported
import { PlantVolumeModel } from 'src/types/PlantVolumesModel';
import api from '../../api/Api';
import { CircularProgress, Box } from '@mui/material';
import CheckCircle from '../../icons/CheckCircle';
import PageContainerLayout from 'src/pages/PageContainerLayout';
import { Logger } from 'src/utils/logger';
import React from 'react'; // Added React import for JSX

const PlantVolumes: FC<React.PropsWithChildren<unknown>> = () => {
  const { customerNumber } = useParams<{ customerNumber: string }>(); // Ensure customerNumber is typed
  const [data, setData] = useState<PlantVolumeModel[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  const [editDescriptor, setEditDescriptor] = useState<EditDescriptor>({});

  const init = async () => {
    if (!customerNumber) return; // Do not proceed if customerNumber is not available
    try {
      let pvs = await api.get<PlantVolumeModel[]>(`/plant/plantvolumes?customerNumber=${customerNumber}`);
      setData(pvs || []);
    } catch (error) {
      Logger.error('Failed to initialize plant volumes', error);
      setData([]);
    }
  };

  useEffect(() => {
    init();
  }, [customerNumber]);

  const saveChanges = async () => {
    if (!customerNumber) return;
    try {
      setSaving(true);
      setSaveSuccess(false);
      await api.post<boolean>(`/Plant/updateplantvolumes?customerNumber=${customerNumber}`, data);
      setSaving(false);
      setSaveSuccess(true);
      setEditDescriptor(undefined);
    } catch (error) {
      Logger.error(error);
      setSaving(false);
    }
  };

  const onPlantVolumesChange = (e: React.ChangeEvent<HTMLSelectElement>, dataItem: PlantVolumeModel) => {
    const newData = data.map((item) => {
      if (item.vendorNumber === dataItem.vendorNumber) {
        return { ...item, selectedVolumeId: e.target.value };
      }
      return item;
    }) as PlantVolumeModel[];
    setData(newData);
  };

  const PlantVolumeCell: FC<GridCellProps> = (props) => {
    const {
      dataItem,
      field,
      className,
      style,
      colSpan,
      // role is not in GridCellProps, so we set it directly
      ariaColumnIndex,
      isSelected,
      columnIndex,
    } = props;

    if (!dataItem || !field || !dataItem[field] || !Array.isArray(dataItem[field])) {
      return (
        <td
          className={className}
          style={style}
          colSpan={colSpan}
          role="gridcell" // Hardcode role for accessibility
          aria-colindex={ariaColumnIndex}
          aria-selected={Boolean(isSelected)} // Ensure boolean
          data-grid-col-index={columnIndex}
        />
      );
    }
    const currentItem = dataItem as PlantVolumeModel;

    return (
      <td
        className={className}
        style={style}
        colSpan={colSpan}
        role="gridcell" // Hardcode role
        aria-colindex={ariaColumnIndex}
        aria-selected={Boolean(isSelected)}
        data-grid-col-index={columnIndex}
      >
        <select value={currentItem.selectedVolumeId || ''} onChange={(e) => onPlantVolumesChange(e, currentItem)} style={{ width: '100%' }}>
          {(currentItem[field] || []).map((itemOption: any, i: number) => (
            <option key={`${itemOption.volumeId}-${i}`} value={itemOption.volumeId}>
              {itemOption.description}
            </option>
          ))}
        </select>
      </td>
    );
  };

  const itemChange = (event: GridItemChangeEvent) => {
    const fieldToUpdate = event.field || '';
    if (!fieldToUpdate || !event.dataItem) return;

    const newData = data.map((item) => (item.vendorNumber === event.dataItem.vendorNumber ? { ...item, [fieldToUpdate]: event.value } : item));
    setData(newData);
  };

  const handleEditChange = (event: GridEditChangeEvent) => {
    setEditDescriptor(event.edit);
  };

  return (
    <PageContainerLayout pageHeaderTitle="Plant Volumes">
      <Box>
        {(!data || data.length === 0) && !saving ? (
          <CircularProgress />
        ) : (
          <Grid
            style={{ maxHeight: '550px', marginTop: '5px', width: '100%' }}
            data={data}
            dataItemKey={'vendorNumber'}
            onItemChange={itemChange}
            edit={editDescriptor}
            onEditChange={handleEditChange}
            editable
          >
            <GridToolbar>
              <button title="Save Changes" className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={saveChanges} disabled={saving}>
                Save Changes
              </button>
              {saving && <CircularProgress style={{ color: 'inherit', marginLeft: '5px' }} size={17} />}
              {saveSuccess && <CheckCircle style={{ marginLeft: '5px', color: 'green' }} />}
            </GridToolbar>
            <GridColumn field="vendorName" title="Vendor Name" width={280} editable={false} />
            <GridColumn field="vendorNumber" title="Vendor Number" width={110} editable={false} />
            <GridColumn field="items" width={360} title="Plant Volumes" cells={{ data: PlantVolumeCell }} editable={false} />
            <GridColumn field="margin" width={110} title="Margin" editor="numeric" editable={true} />
            <GridColumn field="" title="" editable={false} /> {/* Spacer column, Kendo will auto-adjust width or you can give specific small width */}
          </Grid>
        )}
      </Box>
    </PageContainerLayout>
  );
};

export default PlantVolumes;
